import { useEffect, useState } from 'react';

import type { AlgoliaCompanyRecord } from '../../types/algolia-company-record';
import { computeBestCompaniesHubClientDisplay } from './compute-best-companies-hub-client-display';
import { fetchBestCompaniesHubFromApi } from './fetch-best-companies-hub-from-api';

/**
 * After SSR / hydration sources resolve, loads hub companies from the API when still empty
 * (typical on client-side navigation when `useData()` and serialized context lag behind).
 */
export function useBestCompaniesHubClientCompanies(
  resolvedCompanies: AlgoliaCompanyRecord[],
  routeKey: string,
): {
  companies: AlgoliaCompanyRecord[];
  /** True on the client while waiting for `/api/best-companies-hub` after navigation when resolved data is still empty. */
  isLoadingHubCompanies: boolean;
} {
  const [fetched, setFetched] = useState<AlgoliaCompanyRecord[] | null>(null);
  const [clientFetchFinished, setClientFetchFinished] = useState(false);

  useEffect(() => {
    setFetched(null);
    setClientFetchFinished(false);
  }, [routeKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (resolvedCompanies.length > 0) return;

    let cancelled = false;
    void fetchBestCompaniesHubFromApi()
      .then((companies) => {
        if (!cancelled) setFetched(companies);
      })
      .finally(() => {
        if (!cancelled) setClientFetchFinished(true);
      });

    return () => {
      cancelled = true;
    };
  }, [routeKey, resolvedCompanies.length]);

  const isBrowser = typeof window !== 'undefined';
  return computeBestCompaniesHubClientDisplay(
    resolvedCompanies,
    fetched,
    clientFetchFinished,
    isBrowser,
  );
}
